// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore the next line errors only occasionally
import type Account from "../Account";
import type { AlertProps } from "@narmi/design_system";
import type {
  MobileSection,
  WebSectionLeft,
  WebSectionRight,
} from "../dbbl/businessLogic/modules/userDisplayPreferences/types";
import type App from "../App";
import type { Disclosure } from "./Disclosure.types";
import "./models/v1/NafApp.types";

export enum AoCardFundingType {
  AUTHORIZE_NET = "authorize.net",
  STRIPE = "stripe",
}

export enum AccountOpeningFlow {
  NONPERMANENT_RESIDENT = "nonpermanent_resident",
  NONPERMANENT_RESIDENT_JOINT_APPLICATIONS = "nonpermanent_resident_joint_applications",
}

type IncomeFieldValue = {
  field: string;
  required: boolean;
};

type UserGuideType = "onboarding" | "existing";

type IncomeFieldKey = "mortgage_rent" | "industry" | "occupation";

export type AdditionalIncomeReportingFields = {
  [K in IncomeFieldKey]: IncomeFieldValue;
};

export type DashboardCustomizationLockedPositions = {
  mobile_dashboard_item_positions: Partial<Record<MobileSection, number>>;
  web_home_item_positions: Partial<
    Record<WebSectionLeft | WebSectionRight, number>
  >;
};

export type EnabledGlobalSearchEntities = {
  pages: boolean;
  accounts: boolean;
  transactions: boolean;
  cards: boolean;
};

export interface InstitutionFeatures {
  automatic_savings_in_enrollment?: boolean;
  automatic_savings?: boolean | "OPT_IN" | "OPT_OUT";
  array_plus_credit_reporting?: boolean;
  olb_enable_credit_monitor?: boolean;
  olb_enable_marketing_banners?: boolean;
  trends?: "mx" | "mx_master";
  existing_user_account_opening?: boolean;
  instant_account_verification?: string;
  instant_account_verification__account_opening?: string;
  instant_account_verification__digital_banking?: string;
  refer_a_member?: boolean;
  refer_a_member_consumer?: boolean;
  refer_a_member_business?: boolean;
  hide_referral_code_from_aoiob?: boolean;
  hide_referral_code_from_existing_members?: boolean;
  dao_disable_manual_ach?: boolean;
  bao_allow_owner_info_via_email?: boolean;
  cao_allow_joint_applicant_info_via_email?: boolean;
  ao_gtm_datalayer_funding_event?: boolean;
  ao_international_address_google_autopopulate?: boolean;
  ao_nonpermanent_iso_region_code?: boolean;
  ao_nonpermanent_taiwan_iso_region_code_dropdown?: boolean;
  ao_hide_global_funding_limit?: boolean;
  ao_internal_funding?: boolean;
  ao_product_name_heading?: boolean;
  ao_record_nacha_disclosure_acceptance?: boolean;
  ao_welcome_time_estimate?: boolean;
  ao_account_selection_redesign?: boolean;
  ao_account_opening_mobile_redesign?: boolean;
  ao_ach_funding_type?: string;
  ao_card_funding_type?: AoCardFundingType;
  ao_digital_wallet_enabled?: boolean;
  glia?: boolean;
  hide_existing_user_option?: boolean;
  personas?: boolean;
  cao_beneficiaries?: boolean;
  no_beneficial_owners_checkbox_verbiage?: boolean;
  peak_responsible_individual_checkbox_rules?: boolean;
  eligible_products_staff_led?: boolean;
  eligible_products_business_staff_led?: boolean;
  staff_led_bao_multiple_same_product?: boolean;
  staff_led_bao_multiple_checking?: boolean;
  staff_led_decision_assist?: boolean;
  ach?: boolean;
  transfers?: boolean;
  internal_transfers?: boolean;
  p2p?: false | "m2m" | Array<"m2m">;
  bill_pay?: false | "ipay";
  allow_billpay_from_account?: boolean;
  mobile_loan_payments?: boolean;
  wire_templates?: Array<"fedwire">;
  wires?: boolean;
  domestic_wire_file_import?: boolean;
  ach_nacha_passthrough?: boolean;
  "indigo.ACHPassthroughSend_permission"?: string;
  "indigo.ACHPassthroughCollect_permission"?: string;
  international_wires?: boolean;
  international_wires_consumer?: boolean;
  international_wire_templates?: boolean;
  zelle?: string | boolean;
  hold_days?: {
    rdc_hold_days?: number;
  };
  cao_branded_cards?: boolean;
  third_party_enrollment_api?: string;
  business_banking?: boolean;
  personal_banking?: boolean;
  online_banking?: boolean;
  staffer_led_ao_consumer?: boolean;
  staffer_led_ao_business?: boolean;
  bao_personas?: boolean;
  ach_file_import?: boolean;
  ach_payments?: boolean;
  amount_left_to_right?: boolean;
  recipient_drawer?: boolean;
  stop_payment?: boolean;
  stop_payment_by_range?: boolean;
  disable_billpay_native_flow?: boolean;
  disable_billpay_create_person_payee?: boolean;
  cards?: boolean;
  cardhub_sdk?: boolean;
  check_order?: boolean;
  check_image_ocr?: boolean;
  card_travel_notes?: boolean;
  olb_nonpermanent_residents?: boolean;
  olb_nonpermanent_residents_business?: boolean;
  olb_enable_agentiq?: boolean;
  olb_enable_card_atm_limit?: boolean;
  olb_enable_card_pos_limit?: boolean;
  card_max_request_limits?: {
    atm?: number;
    pos?: number;
    user_requests_per_month?: number;
  };
  olb_enable_sensitive_card_info?: boolean;
  limits?: any;
  olb_allow_add_member?: boolean;
  aoiob_third_party?: boolean;
  deprecated_card_freeze_toggle?: boolean;
  nonprofit?: boolean;
  address_change?: boolean;
  support_mailing_address_in_digital_banking?: boolean;
  can_change_email?: boolean;
  can_change_username?: boolean;
  phone_change?: boolean;
  aoiob?: boolean;
  enable_document_filters?: boolean;
  enable_dashboard_layout_redesign?: boolean;
  enable_external_accounts_on_dashboard?: boolean;
  enable_loans_in_transfer_flow?: boolean;
  enable_net_worth_widget?: boolean;
  product_bundles?: boolean;
  onboarding_prompts?: boolean;
  mobile_in_app_provisioning?: boolean;
  enhanced_alerts?: boolean;
  periodic_alerts?: boolean;
  direct_deposit?: false | "none" | "pinwheel" | "atomic";
  topaz?: boolean;
  cao_show_sms_disclosure?: boolean;
  enable_subscription_management?: boolean;
  enable_quick_actions?: boolean;
  disallow_wire_address_pobox?: boolean;
  mobile_services_priority?: string[];
  show_transaction_contact_support?: boolean;
  third_party_login_evaluations?: "off" | "report" | "decision";
  third_party_ach_transaction_evaluations?: "off" | "report" | "decision";
  third_party_wire_transaction_evaluations?: "off" | "report" | "decision";
  third_party_fednow_transaction_evaluations?: "off" | "report" | "decision";
  third_party_swift_wire_transaction_evaluations?:
    | "off"
    | "report"
    | "decision";
  transfers_disable_all_memos?: boolean;
  loan_payment_by_card?: boolean;
  loan_custom_payment?: boolean;
  loan_principal_payment?: boolean;
  card_linked_offers?: boolean;
  card_lock_unlock?: boolean;
  mx_mini_widget_endpoint?: string;
  loan_payment_by_external_ach?: boolean;
  payment_activity?: boolean;
  bulk_payments?: boolean;
  ach_templates?: boolean;
  lending?: boolean;
  allowed_ach_payment_entry_descriptions?: string[];
  cross_sells?: boolean;
  transaction_based_loans?: boolean;
  account_membership?: boolean;
  allow_external_account_linking_without_transferable_accounts?: boolean;
  business_banking_api?: boolean;
  business_banking_api_allowed_scopes?: string[];
  olb_card_controls?: boolean;
  olb_enable_replace_a_card?: boolean;
  expedited_payments?: boolean;
  single_expedite?: boolean;
  invoice_pay?: boolean;
  same_day_ach?: boolean;
  fednow_send?: boolean;
  enable_multiple_joints_feature?: boolean;
  staffer_led_photo_id_to_core?: boolean;
  web_push_provisioning?: boolean;
  ach_payments_collect?: boolean;
  user_initiated_ach_reversals?: boolean;
  model_context_protocol_server?: boolean;
  olb_income_reporting?: boolean;
  additional_income_reporting_fields?: AdditionalIncomeReportingFields;
  multientity_aggregation?: boolean;
  reports?: boolean;
  receipts_enabled?: boolean;
  receipts_manual_assign?: boolean;
  ao_use_landing_page_tftp_disclosure?: boolean;
  enable_account_opening_flows?: AccountOpeningFlow[];
  enable_citizenship_status_question?: boolean;
  existing_user_prompts?: boolean;
  user_guide_type?: UserGuideType | null;
  olb_dashboard_customization?: boolean;
  olb_dashboard_customization_locked_positions?: DashboardCustomizationLockedPositions;
  olb_global_search?: boolean;
  olb_global_search_entities?: EnabledGlobalSearchEntities;
  "indigo.WireTransaction_permission"?: boolean;
  "indigo.FedwireTemplate_permission"?: boolean;
  "indigo.FedwireViaTemplate_permission"?: boolean;
  "indigo.SwiftWireTransaction_permission"?: boolean;
  "indigo.SwiftWireTemplate_permission"?: boolean;
  "indigo.SwiftWireViaTemplate_permission"?: boolean;
  "integrations.base.Card_permission"?: boolean;
  live_ledger?: boolean;
  transaction_enrichment?: boolean;
  olb_loan_autopay?: boolean;
  mobile_dashboard_redesign?: boolean;
  dual_approval?: boolean;
}

export interface SEO {
  type: string;
  provider: string;
  title: string;
  url: string;
  open_new_tab?: boolean;
}

export type InstitutionTransferMenuOptionType =
  | "bill_pay"
  | "funds"
  | "naf"
  | "sso"
  | "p2p"
  | "wires"
  | "zelle"
  | "loan"
  | "ach_payments"
  | "bulk_payments"
  | "ach_file_import"
  | "domestic_wire_file_import";

// This should have a direct corelation with the SupportedSpecialtyOwnershipStructures setting
export enum SpecialtyOwnershipStructureType {
  TRUST = "trust",
  REPRESENTATIVE_PAYEE_ACCOUNT = "representative payee account",
  CONSERVATORSHIP = "conservatorship",
  ESTATE = "estate",
  GUARDIANSHIP = "guardianship",
}
// This should have a direct corelation with the SpecialtyOwnershipStakeholderRole setting
export enum SpecialtyOwnershipStakeholderRole {
  GRANTOR = "grantor",
  TRUSTEE = "trustee",
  REPRESENTATIVE_PAYEE_ROLE = "representative_payee_role",
  OWNER = "owner",
  CONSERVATOR = "conservator",
  CONSERVATEE = "conservatee",
  EXECUTOR = "executor",
  GUARDIAN = "guardian",
  WARD = "ward",
}

export enum LanguagePreferenceType {
  ENGLISH = "English",
  SPANISH = "Spanish",
  AMERICAN_SIGN_LANGUAGE = "American Sign Language",
  ARABIC = "Arabic",
  CHINESE = "Chinese",
  FRENCH = "French",
  GERMAN = "German",
  ITALIAN = "Italian",
  JAPANESE = "Japanese",
  KOREAN = "Korean",
  RUSSIAN = "Russian",
  OTHER = "Other",
  UNKNOWN = "Unknown",
}

export enum TrustType {
  REVOCABLE = "revocable",
  IRREVOCABLE = "irrevocable",
}

export enum TaxIdType {
  SSN = "ssn",
  EIN = "ein",
}

export interface TransferOption {
  type: Exclude<InstitutionTransferMenuOptionType, "naf" | "sso">;
  client_id: never;
}

export interface NAFOption {
  type: "naf";
  client_id: string;
}

export interface SSOOption {
  type: "sso";
  client_id?: never;
  sso_type: string;
  provider: string;
  title: string;
  url: string;
  open_new_tab: boolean;
  icon?: string | null;
  description?: string | null;
}

export type InstitutionTransferMenuOption =
  | TransferOption
  | NAFOption
  | SSOOption;

export interface TransferMenu {
  options: InstitutionTransferMenuOption[];
}

export interface BankingMenu {
  transfer: TransferMenu;
}

export interface AdditionalField {
  name: string;
  value: boolean;
  category: "consumer" | "business";
  options?: Array<string>;
}

export interface DaoFooterDisplaySelector {
  show_membership_text: boolean;
  show_member_dif_text: boolean;
  show_equal_housing_lender_text: boolean;
  show_equal_housing_lender_logo: boolean;
  show_member_dif_logo: boolean;
  show_fdic_ncua_logo: boolean;
  show_powered_by_narmi_text: boolean;
  show_help_phone: boolean;
  show_help_email: boolean;
  /** @deprecated fallback for `show_privacy_policy_link` / `show_terms_link` */
  show_privacy_policy_and_terms_links?: boolean;
  show_privacy_policy_link?: boolean;
  show_terms_link?: boolean;
}

export type DigitalBankingFooterDisplaySelector = {
  show_help_phone?: boolean;
  show_lost_card?: boolean;
  show_help_email?: boolean;
};

type DueDiligenceQuestionType =
  | undefined
  | "freeform"
  | "dropdown"
  | "open input"
  | "yes/no";

export interface AccountActivityAnswerOption {
  displayText: string;
  value?: string;
  followUpQuestions?: DueDiligenceQuestion[];
}
export interface DueDiligenceQuestion {
  question: string;
  rawQuestion?: string;
  portalVersion?: string;
  questionType: DueDiligenceQuestionType;
  options?: Array<string>;
  rawOptions?: Array<string>;
  label?: string; // for freeform or dropdown input
  answerOptions?: AccountActivityAnswerOption[];
  productId?: string | number;
}

export enum DepositInsurer {
  NCUA = "NCUA",
  FDIC = "FDIC",
  NONE = "",
}

declare global {
  namespace API {
    interface Institution {
      stripe_api_version?: string;
      stripe_connected_account_id?: string;
      stripe_public_key?: string;
      stripe_public_key_business?: string;
      ao_digital_wallet_enabled?: boolean;
      bao_responsible_individual?: any;
      api_banking_agreement?: string;
      captcha_public_key?: string;
      show_captcha_bao?: boolean;
      show_captcha_dao?: boolean;
      is_production?: boolean;
      ach_next_cutoff?: number;
      time_zone?: string;
      fdx_api_enabled?: boolean;
      account_alerts_eligible_product_types?: API.ProductType[];
      account_opening_entry_points_config?: API.EntryPointConfig;
      authorize_dot_net_login_id?: string;
      authorize_dot_net_client_key?: string;
      authorize_dot_net_script_url?: string;
      authorize_dot_net_client_key_business?: string;
      authorize_dot_net_login_id_business?: string;
      authorize_dot_net_payment_gateway_id?: string;
      authorize_dot_net_google_pay_merchant_id?: string;
      terms_url?: string;
      features?: InstitutionFeatures;
      dao_ask_additional_fields?: Array<AdditionalField>;
      dao_products_expanded_by_default?: {
        web?: boolean;
        mobile?: boolean;
      };
      oao_occupation_options?: {
        source: "soc" | "custom";
        options?: Array<string>;
      };
      dao_fund_max_amount_debit?: number;
      dao_fund_max_amount_ach?: number;
      dao_fund_max_amount_iav?: number;
      dao_fund_max_amount_transfer?: number;
      bao_fund_max_amount_debit?: number;
      bao_fund_max_amount_ach?: number;
      bao_fund_max_amount_iav?: number;
      bao_fund_max_amount_transfer?: number;
      cao_staffer_led_fund_max_amount_debit?: number;
      cao_staffer_led_fund_max_amount_ach?: number;
      cao_staffer_led_fund_max_amount_transfer?: number;
      bao_staffer_led_fund_max_amount_debit?: number;
      bao_staffer_led_fund_max_amount_ach?: number;
      bao_staffer_led_fund_max_amount_transfer?: number;
      staffer_led_disabled_funding_types?: Record<string, string[]>;
      staffer_led_internal_funding?: boolean;
      dao_fund_card_network?: Array<string>;
      dao_query_params_initial_pageload_whitelist?: Array<string>;
      dao_eligibility_suppression_params?: Array<{
        param: string;
        value: string;
      }>;
      dao_ask_field_occupation_question_setting?: boolean;
      dao_footer_display_selector?: DaoFooterDisplaySelector;
      dao_disable_joint?: boolean;
      dao_alloy_validation_prechecks?: boolean;
      dao_alloy_doc_verification_sdk?: any;
      dao_send_documents_requested_emails_to_joint_applicants?: boolean;
      dao_require_minor_as_primary_applicant?: boolean;
      dao_require_joint_applicant?: boolean;
      dao_require_joint_for_minors?: boolean;
      enable_ai_assisted_alloy_summary?: boolean;
      alloy_dashboard_base_url?: string;
      has_mailing_address?: boolean;
      has_mailing_address_business_applicants?: boolean;
      has_mailing_address_joint_applicants?: boolean;
      allow_po_box_primary_address?: boolean;
      internal_name?: string;
      language_preference_options?: LanguagePreferenceType[];
      long_name: string;
      logo_url?: string;
      bao_disclosures?: Array<Disclosure>;
      dao_courtesy_pay_disclosure?: string;
      dao_courtesy_pay_requires_debit_card?: boolean;
      dao_courtesy_pay_enabled_by_default?: boolean;
      dao_courtesy_pay_below_debit_card?: boolean;
      dao_debit_card_disclosure?: string;
      cao_consent_esign_disclosure?: string;
      bao_consent_esign_disclosure?: string;
      dao_disclosures?: Array<Disclosure>;
      dao_min_ownership_percent_llc?: number;
      dao_min_ownership_percent_corporation?: number;
      help_email?: string;
      routing_number?: string;
      type?: string;
      max_file_upload_size_bytes?: number;
      bao_custom_documentation?: any;
      bao_documentation_mapping_staffer_led?: Record<string, any>;
      bao_success_screen_new_customer_enroll_url?: string;
      bao_success_screen_existing_customer_enroll_url?: string;
      dao_success_screen_new_customer_enroll_url?: string;
      dao_success_screen_existing_customer_enroll_url?: string;
      dao_success_screen_cta_new_member?: string;
      dao_success_screen_cta_returning_member?: string;
      dao_success_screen_show_direct_deposit_above_cta?: boolean;
      dao_website_flow_logo_nav_url?: string;
      dao_theme_overrides?: Record<
        string,
        {
          primary_color?: string;
          primary_color_hex?: string;
          secondary_color?: string;
          secondary_color_hex?: string;
          tertiary_color?: string;
          tertiary_color_hex?: string;
          logo_url?: string;
          favicon_url?: string;
          dao_website_flow_logo_nav_url?: string;
        }
      >;
      require_authorized_signer_with_control_person?: boolean;
      third_party_auto_enrollment?: string;
      third_party_auto_enrollment_form_configuration?: Record<string, string>;
      third_party_enrollment_api?: string;
      primary_color?: string;
      primary_color_hex?: string;
      secondary_color?: string;
      skip_cao_account_activity_questions?: boolean;
      short_name?: string;
      tertiary_color?: string;
      tertiary_color_hex?: string;
      google_tag_manager_id?: string;
      google_tag_manager_whitelist?: Array<string>;
      google_analytics_id?: string;
      allow_po_box_mailing_address?: boolean;
      agentiq_chat_domain?: string;
      favicon_url?: string;
      bao_allow_only_one_control_person?: boolean;
      slcao_allow_only_one_grantor_for_trusts?: boolean;
      bao_supported_business_types?: string[];
      slbao_supported_business_types?: string[];
      member_dif?: boolean;
      dif_logo_url?: string;
      equal_housing_logo_url?: string;
      help_phone?: string;
      privacy_url?: string;
      show_support_footer_link?: boolean;
      digital_banking_footer_display_selectors?: DigitalBankingFooterDisplaySelector;
      cdd_bao_questions?: Array<DueDiligenceQuestion>;
      cdd_cao_questions?: Array<DueDiligenceQuestion>;
      cdd_freeform_answer_allowed_characters?: string;
      nonpermanent_resident_address_allowed_characters?: string;
      nonpermanent_resident_taiwan_iso_subdivisions?: Array<{
        code: string;
        name: string;
        type: string;
      }>;
      prompt_account_number?: boolean;
      prompt_account_number_bao?: boolean;
      bao_neuro_id_site_id?: string;
      cao_neuro_id_site_id?: string;
      cao_fingerprint_config?: {
        api_key?: string;
        proxy_domain?: string;
        [key: string]: string | undefined;
      };
      cao_existing_core_user_applicant_handling?:
        | "USE_EXISTING_MEMBER_ID"
        | "CREATE_NEW_MEMBER_ID"
        | "USER_ASSOCIATE"
        | "USER_ASSOCIATE_OR_NEW";
      core_user_id_term?: string;
      staffer_led_existing_customer_lookup_methods?: (
        | "name_email"
        | "core_user_id"
        | "ssn"
        | "account_number"
      )[];
      no_joint_accounts_just_share_name_records?: boolean;
      application_submitting_timeout?: number;
      s3_images_bucket?: string;
      ao_fund_max_amount_narmi_ach?: number;
      is_demo?: boolean;
      is_local?: boolean;
      mdoc_supported_devices?: string[];
      announcement?: string;
      main_domain?: string;
      digital_account_opening_url?: string;
      business_account_opening_url?: string;
      existing_digital_account_opening_url?: string;
      existing_digital_account_opening_message?: string;
      mobile_login_link_to_dao_url?: boolean;
      mobile_savvy_money_css_override?: string;
      card_phone?: string;
      facebook?: string;
      twitter?: string;
      instagram?: string;
      linkedin?: string;
      card_network?: string;
      card_processor?:
        | "pscu"
        | "dummy"
        | "smart"
        | "cardhub"
        | "card_suite_pro"
        | "none";
      push_provisioning_via_card_processor?: boolean;
      web_push_provisioning_config?: {
        google_integrator_id: string;
        google_environment:
          | "production"
          | "production_using_test_card"
          | "test_using_test_card";
        application_uuid?: string;
      };
      contact_url?: string;
      lost_card_url?: string;
      logout_redirect?: string;
      location_hours_url?: string;
      chat_script?: string;
      ach_in_url?: string;
      ach_out_url?: string;
      service_urls?: string[];
      nav_order?: string[];
      // mobile_navigation - needs React 18 for react native webview props
      address_1?: string;
      address_2?: string;
      address_3?: string;
      ect?: string;
      fdic_ncua_logo_url?: string;
      deposit_insurer?: DepositInsurer;
      mobile_splash_screen_logo_url?: string;
      framework_css_url?: string;
      framework_js_url?: string;
      header_font?: string;
      ach_terms_url?: string;
      rdc_terms_url?: string;
      rdc_info?: string;
      show_enrollment_captcha?: boolean;
      ios_app_id?: string;
      android_app_id?: string;
      esign_url?: string;
      disable_daily_transfer?: boolean;
      disable_semimonthly_transfer?: boolean;
      transfer_disclosure?: string;
      instant_transfer_disclosure?: string;
      ach_payment_disclosure?: string;
      ach_collect_payment_disclosure?: string;
      wire_disclosure?: string;
      international_wire_disclosure?: string;
      swift_fx_quote_disclosure?: string;
      swift_additional_info?: string;
      wire_display_fee?: string;
      international_wire_fee?: Array<Record<string, string>>;
      wire_info?: string;
      stoppayment_info?: string;
      new_customer_url?: string;
      sso_urls?: SEO[];
      banking_url?: string;
      mfa_required?: boolean;
      referral_terms_url?: string;
      referral_offer_text?: string;
      referral_additional_offer_text?: string;
      referral_share_message?: string;
      referral_new_customer_url?: string;
      referral_new_business_customer_url?: string;
      referral_email_subject?: string;
      referral_amount?: number;
      routing_url?: string;
      rdc_instructions?: string;
      keywords?: string[];
      description?: string;
      enrollment_code?: string;
      dao_minimum_applicant_birth_year?: number;
      dao_custom_javascript_setting?: string;
      dao_send_email_testing_notifications?: boolean;
      dao_complete_refresh_seconds?: number;
      dao_minimum_applicant_age?: number;
      staff_led_minimum_applicant_age?: number;
      dao_max_beneficiaries_per_application?: number;
      dao_skip_product_selection?: boolean;
      ao_funding_amount_prefill?: "product_default" | "none";
      cao_show_fdic_logo_funding_screen?: boolean;
      cao_show_fdic_logo_landing_screen?: boolean;
      cao_staffer_led_hold_kyc?: boolean;
      cao_staffer_led_skip_kyc?: boolean;
      dao_staffer_led_enable_referral_code?: {
        bao: boolean;
        cao: boolean;
      };
      dao_initial_hold_days_card?: number;
      dao_initial_hold_days_ach?: number;
      dao_referral_offer_amount?: number;
      dao_referral_terms_url?: string;
      ao_force_full_application_flow?: boolean;
      ao_disable_proxy_applicant_pii_entry?: boolean;
      ao_alloy_journey_token?: string;
      ao_welcome_time_estimate?: string;
      ao_search_engine_robots?: string;
      business_banking_enabled?: boolean;
      external_accounts_allows_business_purpose?: boolean;
      sudo_mode_required_for_wires?: boolean;
      sudo_mode_required_for_ach_payments?: boolean;
      sudo_mode_required_for_add_member?: boolean;
      sudo_mode_required_for_sensitive_card_info?: boolean;
      sudo_mode_required_for_email_change?: boolean;
      sudo_mode_required_for_phone_change?: boolean;
      sudo_mode_required_for_username_change?: boolean;
      sudo_mode_required_for_add_external_account?: boolean;
      sudo_mode_required_for_external_transfers?: boolean;
      sudo_mode_required_for_bulk_payments?: boolean;
      sudo_mode_required_for_user_initiated_ach_reversals?: boolean;
      sudo_mode_required_for_m2m_transfer?: boolean;
      online_banking_custom_javascript?: string;
      ach_allows_pull?: boolean;
      ach_allows_push?: boolean;
      ach_use_available_balance_for_scheduled_push?: boolean;
      custom_footer?: string;
      auth_username?: boolean;
      plaid_api_layer_template_id?: string;
      permitted_mfa_methods?: string[];
      enrollment_mfa_methods?: string[];
      banker_led_ao_locations?: string[];
      staffer_led_metadata_fields?: {
        key_name: string;
        key_display_name: string;
        dropdown_values: {
          value_name: string;
          value_display_name: string;
        }[];
      }[];
      institution_public_google_places_url?: string;
      loan_pay_by_card_fee?: number | string;
      core_update_estatement?: boolean;
      show_checkbox_on_enrollment_preferences_page?: boolean;
      require_viewing_sample_pdf_for_paperless_enrollment?: boolean;
      include_esign_disclosure_link_on_review_disclosures_page?: boolean;
      core_update_address?: boolean;
      forgot_password_url?: string;
      help_card_phone?: string;
      register_url?: string;
      default_rdc_hold_days?: number;
      ao_allow_skip_funding?: boolean;
      banking_menus?: BankingMenu;
      apps?: App[];
      tiles?: API.NarmiTile[];
      display_bao_footer?: boolean;
      cao_beneficiaries?: boolean;
      cao_beneficiaries_for_new_applicants_only?: boolean;
      no_beneficial_owners_checkbox_verbiage?: boolean;
      beneficiaries_relationship_codes?: string[][];
      add_beneficiaries_to_all_accounts?: boolean;
      cdd_business_slao_questions?: DueDiligenceQuestion[];
      cdd_consumer_slao_questions?: DueDiligenceQuestion[];
      wire_reason_types?: string[];
      third_party_banking_login_url?: string;
      license_keys?: LicenseKeys;
      mcp_server_hostname?: string;
      dcr_refresh_token_lifespan?: number;
      approved_screen_enable_account_info?: {
        direct_deposit_number: boolean;
        account_number: boolean;
        routing_number: boolean;
      };
      submitted_screen_show_account_info_for_cds?: boolean;
      travel_notes_excluded_countries?: string[];
      institution_account_alerts_eligible_product_types?: ProductType[];
      separate_primary_and_joint_contact_info?: boolean;
      hide_primary_contact_info?: boolean;
      aoiob_allow_joint_applicants?: boolean;
      aoiob_hide_login_button?: boolean;
      manual_funding_routing_numbers_denylist?: string[];
      manual_funding_routing_numbers_product_type_denylist?: string[];
      supported_specialty_ownership_structures?: SpecialtyOwnershipStructureType[];
      ach_business_description?: string;
      /**
       * Only allowed `checking` and `savings`
       */
      direct_deposit_eligible_product_types?: API.ProductType[];
      direct_deposit_provider?: "pinwheel" | "atomic" | "none";
      cao_direct_deposit_eligible_product_types?: API.ProductType[];
      cao_direct_deposit_provider?: "pinwheel" | "none";
      collect_topaz_sig_string?: boolean;
      manual_id_entry?: boolean;
      manual_id_entry_issuance_date?: boolean;
      manual_id_entry_id_types?: string[];
      cao_staffer_led_manual_id_category_1_id_types?: string[];
      cao_staffer_led_manual_id_category_1_required_count?: number;
      cao_staffer_led_manual_id_category_2_id_types?: string[];
      cao_staffer_led_manual_id_category_2_required_count?: number;
      onboarding_remind_me_later_days?: number;
      cao_aoiob_skip_membership_page?: boolean;
      cao_aoiob_hide_review_applicant_details?: boolean;
      card_linked_offers_enrollment_terms_of_service?: string;
      card_linked_offers_exclude_hsa?: boolean;
      enable_loan_payments_web?: boolean;
      enable_loan_payments_mobile?: boolean;
      supports_sso_tax_document?: boolean;
      supports_sso_per_account?: boolean;
      loan_payment_overdue_days?: number;
      lending_url?: string;
      max_days_approval_grace_period?: number;
      minimum_sole_applicant_age?: number;
      enable_multiple_joints_feature?: boolean;
      staffer_led_photo_id_to_core?: boolean;
      staffer_led_applicant_debit_cards?: boolean;
      public_announcement_configuration?: PublicAnnouncementConfigProps;
      ao_animated_background_color?: "primary" | "secondary" | "tertiary";
      account_details_information_blacklist?: Record<string, string[]>;
    }

    type GetInstitutionResponse = {
      institution: Institution;
    };

    /**
     * Services that required license keys
     * Returned in institution settings under the "license_keys" record
     */
    type LicensedService = "misnap";
    type LicenseKeys = Record<LicensedService, string> | Record<string, never>;

    export interface BaseMenuItem {
      feature_flag?: string;
      superscript?: string;
    }

    export interface AppMenuItem extends BaseMenuItem {
      app: App;
    }

    export interface LinkMenuItem extends BaseMenuItem {
      url: string;
      openNewTab?: boolean;
    }

    export interface AccountMenuItem extends BaseMenuItem {
      accounts: Account[];
    }

    export interface NAFMenuItem extends BaseMenuItem {
      naf: true;
    }

    export interface AOIOBMenuItem extends BaseMenuItem {
      aoiob: true;
    }

    export interface SidebarMenuOption {
      icon?: string;
      url?: string;
      key?: string;
      new_tab?: boolean;
      feature_flags?: string[];
      superscript?: string;
      sort_order?: number;
    }

    export interface SidebarMenuItem {
      icon?: string;
      url?: string;
      key?: string;
      expandable?: boolean;
      feature_flags?: string[];
      naf_apps?: boolean;
      naf_client_id?: string;
      aoiob?: boolean;
      naf_sort_order?: number;
      aoiob_sort_order?: number;
      options?: Record<string, SidebarMenuOption>;
    }

    /**
     * A resolved backend sub-item (within a group). Replaces AppSidebarMenuOption.
     * Only represents standard route-based links from the backend config.
     * NAF and AOIOB sub-items are added client-side in azul and use
     * the SidebarOptionItem discriminated union in sidebar.types.ts.
     */
    export type SidebarMenuSubItem = {
      title: string;
      icon?: string;
      route: string;
      newTab: boolean;
      sortOrder?: number;
    };

    /** A resolved sidebar menu item (top-level row). Replaces AppSidebarMenuItem. */
    export type SidebarMenuItem_Resolved = {
      title: string;
      key?: string;
      icon?: string;
      route?: string;
      /** When true, NAF apps should be appended as sub-items (client-side in azul) */
      showNAFApps: boolean;
      /** When true, AOIOB link should be appended as a sub-item (client-side in azul) */
      showAOIOB: boolean;
      /** Position of the NAF apps among `subItems`, on the same scale as `sortOrder`; last when unset */
      nafSortOrder?: number;
      /** Position of the AOIOB link among `subItems`, on the same scale as `sortOrder`; last when unset */
      aoiobSortOrder?: number;
      /** If set, this item is a standalone NAF app -- resolve from institution.apps */
      nafClientId?: string;
      /** Standard sub-items from backend config (route-based links only) */
      subItems: SidebarMenuSubItem[];
    };

    export type NestedMenu = {
      menu: TopMenuType;
    };

    export type AOEntryPoint = {
      name: string;
      platform: string;
      target: string;
    };

    type EntryPointConfig = {
      accounts_menu?: AOEntryPoint[];
      balances_dropdown?: AOEntryPoint[];
    };

    export type AnyMenuItem =
      | AppMenuItem
      | LinkMenuItem
      | AccountMenuItem
      | NAFMenuItem
      | NestedMenu
      | AOIOBMenuItem;

    export interface TopMenuType {
      [key: string]: AnyMenuItem;
    }

    export interface SidebarMenuType {
      [key: string]: SidebarMenuItem;
    }
  }
}

export type Institution = API.Institution;

export type InstitutionFeatureFlags = keyof API.Institution;

export interface RenderItemMenuProps<T> {
  name: string;
  itemProps: T;
}

export interface PublicAnnouncementConfigProps {
  message: string;
  kind: NonNullable<AlertProps["kind"]>;
  end_date?: string;
}
